/**
 * Time-of-day host — local clock plus a simple sun model.
 * Exposed as ctx.time.
 *
 * Wallpapers, clock widgets and day/night theming all need the same question
 * answered ("where are we in the day, and how high is the sun"), so the model
 * lives here once rather than in each module. Deliberately astronomy-free: no
 * geolocation permission, no ephemeris — sunrise/sunset are configurable hours
 * and altitude is a smooth curve between them. Predictable beats precise for
 * driving a gradient.
 */

export type DayPhase = "night" | "dawn" | "morning" | "day" | "dusk" | "evening";

export type TimeSnapshot = {
  /** Wall-clock date (or the override, when one is set). */
  date: Date;
  /** 0..1 through the local day. 00:00 → 0, 12:00 → 0.5. */
  dayFraction: number;
  /** Local hours as a float, e.g. 13.5 for 13:30. */
  hours: number;
  /**
   * Sun altitude, −1 (deep night) .. 1 (solar noon). 0 at sunrise/sunset —
   * the number most visuals actually want.
   */
  sunAltitude: number;
  /** Sun position across the sky, 0 at sunrise → 1 at sunset. Clamped. */
  sunAzimuth: number;
  phase: DayPhase;
  /** True while an override is active (previewing, not the real clock). */
  overridden: boolean;
};

export type TimeApi = {
  now(): Date;
  get(): TimeSnapshot;
  /**
   * Called immediately, then whenever the snapshot meaningfully changes.
   * `intervalMs` defaults to 30s — enough for a clock, cheap for a wallpaper
   * that samples `get()` every frame anyway.
   */
  subscribe(fn: (s: TimeSnapshot) => void, opts?: { intervalMs?: number }): () => void;
  /** Sunrise/sunset hours driving the model. Defaults 6 and 18. */
  setSunTimes(sunriseHour: number, sunsetHour: number): void;
  /**
   * Pin the clock to an hour (0..24) for previewing a day cycle without
   * waiting for it. `null` returns to the real clock.
   */
  setOverride(hours: number | null): void;
  getOverride(): number | null;
};

const SUN_KEY = "arclog.time.sun";

let sunrise = 6;
let sunset = 18;
let override: number | null = null;

try {
  const raw = localStorage.getItem(SUN_KEY);
  if (raw) {
    const parsed = JSON.parse(raw) as { sunrise?: number; sunset?: number };
    if (typeof parsed.sunrise === "number") sunrise = parsed.sunrise;
    if (typeof parsed.sunset === "number") sunset = parsed.sunset;
  }
} catch {
  /* defaults are fine */
}

function phaseFor(alt: number, hours: number): DayPhase {
  if (alt > 0.55) return "day";
  if (alt > 0.12) return hours < 12 ? "morning" : "day";
  if (alt > -0.12) return hours < 12 ? "dawn" : "dusk";
  if (alt > -0.45) return hours < 12 ? "dawn" : "evening";
  return "night";
}

function computeSnapshot(): TimeSnapshot {
  const real = new Date();
  const hours =
    override !== null
      ? ((override % 24) + 24) % 24
      : real.getHours() + real.getMinutes() / 60 + real.getSeconds() / 3600;

  const date = new Date(real);
  if (override !== null) {
    date.setHours(Math.floor(hours), Math.floor((hours % 1) * 60), 0, 0);
  }

  const dayLength = Math.max(0.5, sunset - sunrise);
  const nightLength = Math.max(0.5, 24 - dayLength);

  // Half-sine above the horizon between sunrise and sunset, and a mirrored
  // half-sine below it overnight — continuous and zero at both crossings.
  let sunAltitude: number;
  let sunAzimuth: number;
  if (hours >= sunrise && hours <= sunset) {
    const t = (hours - sunrise) / dayLength;
    sunAltitude = Math.sin(t * Math.PI);
    sunAzimuth = t;
  } else {
    const since = hours > sunset ? hours - sunset : hours + (24 - sunset);
    const t = since / nightLength;
    sunAltitude = -Math.sin(t * Math.PI);
    sunAzimuth = hours > sunset ? 1 : 0;
  }

  return {
    date,
    dayFraction: hours / 24,
    hours,
    sunAltitude,
    sunAzimuth,
    phase: phaseFor(sunAltitude, hours),
    overridden: override !== null,
  };
}

type Listener = { fn: (s: TimeSnapshot) => void; timer: number };

class TimeBus {
  private listeners = new Set<Listener>();

  get(): TimeSnapshot {
    return computeSnapshot();
  }

  subscribe(fn: (s: TimeSnapshot) => void, opts?: { intervalMs?: number }): () => void {
    const every = Math.max(1000, opts?.intervalMs ?? 30_000);
    const rec: Listener = {
      fn,
      timer: (typeof window !== "undefined"
        ? window.setInterval(() => fn(computeSnapshot()), every)
        : 0) as number,
    };
    this.listeners.add(rec);
    fn(computeSnapshot());
    return () => {
      clearInterval(rec.timer);
      this.listeners.delete(rec);
    };
  }

  /** Push a snapshot to everyone now — used when the model itself changes. */
  emit() {
    const s = computeSnapshot();
    for (const l of this.listeners) l.fn(s);
  }

  setSunTimes(rise: number, set: number) {
    sunrise = Math.min(23, Math.max(0, rise));
    sunset = Math.min(24, Math.max(sunrise + 0.5, set));
    try {
      localStorage.setItem(SUN_KEY, JSON.stringify({ sunrise, sunset }));
    } catch {
      /* */
    }
    this.emit();
  }

  setOverride(hours: number | null) {
    override = hours === null || !Number.isFinite(hours) ? null : hours;
    this.emit();
  }

  getOverride(): number | null {
    return override;
  }
}

export const timeBus = new TimeBus();

export function createTimeApi(): TimeApi {
  return {
    now: () => timeBus.get().date,
    get: () => timeBus.get(),
    subscribe: (fn, opts) => timeBus.subscribe(fn, opts),
    setSunTimes: (rise, set) => timeBus.setSunTimes(rise, set),
    setOverride: (hours) => timeBus.setOverride(hours),
    getOverride: () => timeBus.getOverride(),
  };
}
